const FOCUSABLE_SELECTOR =
  "summary, a[href], button:not([disabled]), [tabindex]:not([tabindex^='-']), area, input:not([type='hidden']):not([disabled]), select:not([disabled]), textarea:not([disabled]), object, iframe";
const CAROUSEL_ROOT_SELECTOR =
  '[data-nw-swiper], [data-nw-swiper-cards], [data-nw-reviews-carousel], slideshow-component, layered-slideshow-component, .swiper';

/**
 * Finds the first matching element after the current DOM branch.
 *
 * @param {HTMLElement | null} element
 * @param {(sibling: Element) => HTMLElement | null} findMatch
 * @returns {HTMLElement | null}
 */
function findFollowingElement(element, findMatch) {
  let current = element;

  while (current) {
    for (let sibling = current.nextElementSibling; sibling; sibling = sibling.nextElementSibling) {
      const match = findMatch(sibling);
      if (match) return match;
    }

    current = current.parentElement;
  }

  return null;
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isVisible(element) {
  return !element.closest('[hidden], [inert], [aria-hidden="true"]') && Boolean(element.getClientRects().length);
}

/**
 * @param {HTMLElement} trigger
 * @returns {HTMLElement | null}
 */
function getCarouselRoot(trigger) {
  const closestCarouselRoot = trigger.closest(CAROUSEL_ROOT_SELECTOR);
  if (closestCarouselRoot instanceof HTMLElement) return closestCarouselRoot;

  const siblingCarouselRoot = trigger.nextElementSibling?.matches(CAROUSEL_ROOT_SELECTOR)
    ? trigger.nextElementSibling
    : trigger.parentElement?.querySelector(`:scope > ${CAROUSEL_ROOT_SELECTOR}`);

  if (siblingCarouselRoot instanceof HTMLElement) return siblingCarouselRoot;

  return findFollowingElement(trigger.parentElement, (sibling) => {
    if (sibling instanceof HTMLElement && sibling.matches(CAROUSEL_ROOT_SELECTOR)) return sibling;

    const nestedCarouselRoot = sibling.querySelector(CAROUSEL_ROOT_SELECTOR);
    return nestedCarouselRoot instanceof HTMLElement ? nestedCarouselRoot : null;
  });
}

/**
 * @param {HTMLElement} carouselRoot
 * @returns {HTMLElement | null}
 */
function getNextFocusableElement(carouselRoot) {
  for (const element of document.querySelectorAll(FOCUSABLE_SELECTOR)) {
    if (!(element instanceof HTMLElement) || !isVisible(element)) continue;

    const relation = carouselRoot.compareDocumentPosition(element);
    const followsCarousel = Boolean(relation & Node.DOCUMENT_POSITION_FOLLOWING);
    const isInsideCarousel = Boolean(relation & Node.DOCUMENT_POSITION_CONTAINED_BY);

    if (followsCarousel && !isInsideCarousel) return element;
  }

  return null;
}

/**
 * @param {HTMLElement} carouselRoot
 * @returns {HTMLElement | null}
 */
function getNextFallbackElement(carouselRoot) {
  return findFollowingElement(carouselRoot, (sibling) => {
    return sibling instanceof HTMLElement && isVisible(sibling) ? sibling : null;
  });
}

/**
 * @param {HTMLElement} element
 */
function focusAndReveal(element) {
  element.focus({ preventScroll: true });
  element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

document.addEventListener('click', (event) => {
  const trigger = event.target instanceof HTMLElement ? event.target.closest('[data-nw-skip-carousel]') : null;

  if (!(trigger instanceof HTMLElement)) return;

  const carouselRoot = getCarouselRoot(trigger) ?? trigger.closest('.shopify-section, section, [role="region"]');

  if (!(carouselRoot instanceof HTMLElement)) return;

  const nextFocusableElement = getNextFocusableElement(carouselRoot);

  if (nextFocusableElement) {
    focusAndReveal(nextFocusableElement);
    return;
  }

  const fallbackElement = getNextFallbackElement(carouselRoot);

  if (!(fallbackElement instanceof HTMLElement)) return;

  const previousTabindex = fallbackElement.getAttribute('tabindex');
  fallbackElement.setAttribute('tabindex', '-1');
  focusAndReveal(fallbackElement);

  fallbackElement.addEventListener(
    'blur',
    () => {
      if (previousTabindex == null) fallbackElement.removeAttribute('tabindex');
      else fallbackElement.setAttribute('tabindex', previousTabindex);
    },
    { once: true }
  );
});
